/**
 * browser-loop — transport (the actual POST).
 *
 * Two paths:
 *
 *   1. HOT PATH — fetch with `keepalive: true`.
 *      Works cross-origin with the `Authorization` header (CORS preflight
 *      cached after the first batch). The keepalive flag lets the request
 *      survive page unload up to 64KB, which matches sendBeacon's cap
 *      exactly. Used for every batch except the very last on unload.
 *
 *   2. UNLOAD PATH — same fetch+keepalive, called from beforeunload.
 *      We could use `navigator.sendBeacon` here, but sendBeacon cannot
 *      reliably carry the `Authorization: Bearer dev_*` header
 *      cross-origin (browser spec restricts beacon headers). So we
 *      stick with keepalive fetch. The bearer goes in the header, the
 *      browser dispatches before tearing down.
 *
 * Why we don't use sendBeacon at all (Council does for some signals):
 *   Council ships OTLP through a SAME-ORIGIN BFF route at
 *   /api/telemetry/v1/<signal>, so sendBeacon's no-custom-header rule
 *   doesn't bite — auth flows via cookie. HammerHead uses cross-origin
 *   directly with a localStorage bearer, so the BFF dance is wasted
 *   hops. `fetch(..., {keepalive:true})` is the 2026-correct answer.
 *
 * Retry posture (closed-loop §V.7 pipe-defense fourth rule —
 * rate-limit returns 429, don't retry-loop; only retry transient 5xx):
 *   - 2xx → success, done
 *   - 4xx → no retry (caller's fault, retrying won't help)
 *   - 5xx → up to 2 retries with backoff 500ms, 1500ms
 *   - network error → 1 retry with 500ms backoff
 */

import type { BrowserLoopConfig } from "./config";

export interface BatchPostResult {
  ok: boolean;
  status: number;
  responseBody: unknown | null;
  /** Network-error if no HTTP response at all. */
  transportError?: string;
  durationMs: number;
}

const HOT_RETRY_DELAYS = [500, 1500] as const;
const UNLOAD_RETRY_DELAYS: readonly number[] = [];

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

async function attempt(
  url: string,
  body: string,
  bearer: string | null,
): Promise<BatchPostResult> {
  const started = performance.now();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (bearer) headers["Authorization"] = `Bearer ${bearer}`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers,
      body,
      keepalive: true,
      credentials: "omit",
    });
    let responseBody: unknown | null = null;
    if (res.status !== 204) {
      try {
        responseBody = await res.json();
      } catch {
        responseBody = null;
      }
    }
    return {
      ok: res.ok,
      status: res.status,
      responseBody,
      durationMs: performance.now() - started,
    };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      responseBody: null,
      transportError:
        err instanceof Error ? `${err.name}: ${err.message}` : String(err),
      durationMs: performance.now() - started,
    };
  }
}

/**
 * Post a batch with retry policy appropriate to context.
 *
 * @param mode 'hot' — normal flush; full retry budget.
 *             'unload' — beforeunload; no retries (page is leaving).
 */
export async function postBatch(
  config: BrowserLoopConfig,
  payload: object,
  mode: "hot" | "unload" = "hot",
): Promise<BatchPostResult> {
  const body = JSON.stringify(payload);
  const delays = mode === "hot" ? HOT_RETRY_DELAYS : UNLOAD_RETRY_DELAYS;

  let result = await attempt(config.ingestUrl, body, config.bearer);

  for (const delayMs of delays) {
    // Only retry on transient failure modes.
    const retryable =
      result.status === 0 || // transport error
      result.status === 429 || // honor backend's Retry-After (we just delay fixed)
      (result.status >= 500 && result.status <= 599);
    if (!retryable) break;
    await sleep(delayMs);
    result = await attempt(config.ingestUrl, body, config.bearer);
  }

  return result;
}
